import React from "react";
import styled from "styled-components";
import { Alert, Button, Card, Col, Row, Skeleton, Space, Tag, Typography } from "antd";
import RevenueLeakageFunnel from "./RevenueLeakageFunnel.jsx";
import QuoteConversionWidget from "./QuoteConversionWidget.jsx";
import LeadResponseWidget from "./LeadResponseWidget.jsx";
import SourceCampaignWidget from "./SourceCampaignWidget.jsx";
import DashboardDrilldownDrawer from "./DashboardDrilldownDrawer.jsx";
import useDashboardDrilldown from "../hooks/useDashboardDrilldown.js";

const { Text, Title } = Typography;

const ShellSection = styled.div`
  margin-bottom: 24px;
`;

const SlotCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const SalesDashboardShell = ({
  metricGroups,
  dashboardFilters,
  loading = false,
  error = null,
  canView = false,
}) => {
  const drilldownState = useDashboardDrilldown(dashboardFilters);

  if (!canView) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Sales dashboard access required"
        description="You need leads, quotes, or analytics reporting permissions to view this dashboard."
      />
    );
  }

  if (loading) {
    return (
      <ShellSection data-testid="sales-dashboard-loading">
        <Skeleton active paragraph={{ rows: 6 }} />
      </ShellSection>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Sales dashboard could not load"
        description={error}
      />
    );
  }

  const salesGroup = metricGroups?.sales;
  const qualifiedWithoutQuoteCount =
    salesGroup?.metadata?.leadResponse?.qualifiedWithoutQuote || 0;

  return (
    <ShellSection data-testid="sales-dashboard-shell">
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <SlotCard>
          <Space direction="vertical" size={8}>
            <Title level={4}>Sales Dashboard</Title>
            <Text type="secondary">
              Conversion, response, quote movement, source, campaign, and follow-up
              signals using the shared dashboard filters.
            </Text>
            <FilterSummary dashboardFilters={dashboardFilters} />
          </Space>
        </SlotCard>

        <RevenueLeakageFunnel
          funnel={salesGroup?.metadata?.revenueLeakageFunnel}
          loading={false}
        />

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <QuoteConversionWidget
              quoteConversion={salesGroup?.metadata?.quoteConversion}
            />
          </Col>
          <Col xs={24} lg={8}>
            <LeadResponseWidget
              leadResponse={salesGroup?.metadata?.leadResponse}
              onOpenDrilldown={drilldownState.openDrilldown}
            />
          </Col>
          <Col xs={24} lg={8}>
            <SourceCampaignWidget
              sourceCampaign={salesGroup?.metadata?.sourceCampaign}
            />
          </Col>
        </Row>

        <SlotCard
          title="Sales Drilldowns"
          extra={
            qualifiedWithoutQuoteCount > 0 ? (
              <Tag color="orange">{qualifiedWithoutQuoteCount} pending quotes</Tag>
            ) : null
          }
        >
          <Space direction="vertical" size={8}>
            <Text type="secondary">
              Investigate revenue leakage from qualified leads that have not received a
              quote yet.
            </Text>
            <Button
              type="primary"
              onClick={() =>
                drilldownState.openDrilldown("qualified_leads_without_quotes")
              }
              disabled={qualifiedWithoutQuoteCount === 0}
            >
              Open unquoted qualified leads
            </Button>
            {qualifiedWithoutQuoteCount === 0 && (
              <Text type="secondary">
                No unquoted qualified leads for the selected filters.
              </Text>
            )}
          </Space>
        </SlotCard>
        <DashboardDrilldownDrawer
          open={drilldownState.open}
          onClose={drilldownState.closeDrilldown}
          drilldown={drilldownState.drilldown}
          loading={drilldownState.loading}
          error={drilldownState.error}
        />
      </Space>
    </ShellSection>
  );
};

const FilterSummary = ({ dashboardFilters }) => {
  const time = dashboardFilters?.time || {};
  const activeFilters = [
    ["Date", time.datePreset || "last_7_days"],
    ["Route", dashboardFilters?.operational?.route],
    ["Source", dashboardFilters?.commercial?.leadSource],
    ["Campaign", dashboardFilters?.commercial?.campaign],
  ].filter(([, value]) => value && value !== "all");

  return (
    <Space size={[8, 8]} wrap>
      {activeFilters.map(([label, value]) => (
        <Tag key={label} color="blue">
          {label}: {value}
        </Tag>
      ))}
    </Space>
  );
};

export default SalesDashboardShell;
